'use client'

import {
    AUTH_SCHEMA,
    AUTH_STEP_QUERY_PARAMS,
    AUTH_STEPS,
    IAuthFormData,
    IAuthStep,
    IAuthStepQueryParams,
} from '@/features/auth/helpers/auth-constants'
import { run } from 'shared'
import { AuthSignInStep } from '@/features/auth/steps/auth-sign-in-step'
import { AuthCodeVerificationStep } from '@/features/auth/steps/auth-code-verification-step'
import { useAuthPageContext } from '@/features/auth/auth-context'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AuthSignupStep } from '@/features/auth/steps/auth-signup-step'
import { useMemo } from 'react'
import { useQueryParams } from '@/utils/url/use-query-params'
import { StepHeader } from '@/components/steps/step-header'
import { cn } from '@/utils/styles/cn'

type IAuthStepsProps = {
    modal?: boolean
}

export const AuthSteps = ({ modal = false }: IAuthStepsProps) => {
    const { currentStep, stateParams } = useAuthPageContext()

    const { getParam } = useQueryParams<IAuthStepQueryParams>()

    const methods = useForm<IAuthFormData>({
        resolver: zodResolver(AUTH_SCHEMA),
        defaultValues: {
            name: '',
            email: stateParams[AUTH_STEP_QUERY_PARAMS.Email] || getParam(AUTH_STEP_QUERY_PARAMS.Email) || '',
        },
    })

    const authStepHeaderProps = useMemo(() => getAuthStepHeaderPropsByStep(currentStep), [currentStep])

    return (
        <div
            className={cn(
                'flex flex-col w-full max-md:justify-start md:justify-center',
                modal ? 'md:min-w-[400px]' : 'h-full my-auto max-w-[390px]'
            )}
        >
            <FormProvider {...methods}>
                <div
                    className={cn(
                        'flex flex-col items-start md:items-center w-full md:mt-[-10%]',
                        modal ? 'md:mt-0 md:items-stretch' : ''
                    )}
                >
                    {!modal && <StepHeader {...authStepHeaderProps} className="max-md:mb-6" />}

                    <div className="w-full">
                        {run(() => {
                            switch (currentStep) {
                                case AUTH_STEPS.SignUp: {
                                    return <AuthSignupStep />
                                }

                                case AUTH_STEPS.SignIn: {
                                    return <AuthSignInStep />
                                }

                                case AUTH_STEPS.CodeVerification: {
                                    return <AuthCodeVerificationStep />
                                }

                                default: {
                                    currentStep satisfies never

                                    return null
                                }
                            }
                        })}
                    </div>
                </div>
            </FormProvider>
        </div>
    )
}

export const getAuthStepHeaderPropsByStep = (step: IAuthStep) => {
    switch (step) {
        case AUTH_STEPS.SignUp: {
            return {
                title: 'Create your free account',
                subtitle: 'Get started in under a minute.',
            }
        }

        case AUTH_STEPS.SignIn: {
            return {
                title: 'Log back in to Superscale',
                subtitle: 'Pick up exactly where you left off.',
            }
        }

        case AUTH_STEPS.CodeVerification: {
            return {
                title: 'Enter verification code',
                subtitle: "We've sent a 6-digit code to your email.",
            }
        }

        default: {
            step satisfies never

            return {
                title: '',
                subtitle: '',
            }
        }
    }
}
